import React, { useState, useEffect } from "react";
import ReusableDataTable from "../../hooks/ReusableDataTable";
import { Button, Label, Modal, TextInput, Select, Badge } from "flowbite-react";
import { FaPen } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import useInputValidation from "../../hooks/useInputValidation";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import axios from "axios";
import { useSidebar } from "../../context/SidebarOpenContext";
import '../../style/model.css';

// Export Libraries
import jsPDF from "jspdf";
import "jspdf-autotable";

function RawMaterial() {
    const [AddModal, setAddModal] = useState(false);
    const [EditModal, setEditModal] = useState(false);
    const [DeleteModal, setDeleteModal] = useState(false);
    const [Input, setInput] = useState({});
    const [RawMaterial, setRawMaterial] = useState([]);
    const [Sizes, setSizes] = useState([]);
    const { errors, handleChange, resetErrors } = useInputValidation();
    const { isSidebarOpen } = useSidebar();
    const [trimsTypeFilter, setTrimsTypeFilter] = useState("all");
    // ✅ Modified Helper: Added 'includeSymbol' flag (default true for UI)
    const formatCurrency = (num, includeSymbol = true) => {
        if (includeSymbol) {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(num);
        } else {
            // Returns just the formatted number (e.g., 1,65,000.00) without ₹
            return new Intl.NumberFormat('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(num);
        }
    };

    useEffect(() => {
        fetchRawMaterial();
    }, []);

    const onChangeValue = (e) => {
        handleChange(e);
        setInput({ ...Input, [e.target.name]: e.target.value });
    }

    const fetchRawMaterial = async () => {
        try {
            const response = await axios.get("master/raw_material_stocks");
            const RawMaterialData = response.data;
            setRawMaterial(RawMaterialData);
        } catch (error) {
            console.error("Error fetching RawMaterial data:", error);
        }
    };

    const fetchSizes = async () => {
        try {
            const response = await axios.get("master/size_variants");
            setSizes(response.data);
        } catch (error) {
            console.error("Error fetching sizes:", error);
        }
    };

    const addRawMaterial = async (event) => {
        event.preventDefault();
        try {
            axios
                .post(`master/raw_material_stocks`, {
                    raw_material_name: Input.rname,
                    stock_count: Input.stock,
                    single_qty_rate: Input.rate || 0, // ✅ Added Rate field
                    unit: Input.unit,
                    trims_type: Input.trims_type,
                    status: Input.status,
                    size_id: Input.size_id,
                })
                .then((res) => {
                    if (res.data.success === 1) {
                        Swal.fire({
                            icon: "success",
                            text: "Trims Created",
                            type: "success",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                        setAddModal(false);
                        setInput({}); // Clear inputs
                        fetchRawMaterial();
                    } else if (res.data.success === 2) {
                        Swal.fire({
                            icon: "error",
                            text: "RawMaterial already exists...",
                            type: "error",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: "Server Problem. Please Try Again...",
                            type: "error",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                    }
                    return;
                });
        } catch (error) {
            throw error;
        }
    };

    const UpdateRawMaterial = async (event) => {
        event.preventDefault();

        try {
            axios
                .put(`master/raw_material_stocks/${Input.id}`, {
                    raw_material_name: Input.rname,
                    stock_count: Input.stock,
                    single_qty_rate: Input.rate || 0, // ✅ Added Rate field
                    unit: Input.unit,
                    trims_type: Input.trims_type,
                    status: Input.status,
                    size_id: Input.size_id,
                    raw_material_stock_id: Input.id
                })
                .then((res) => {
                    if (res.data.success === 1) {
                        Swal.fire({
                            icon: "success",
                            text: "Trims Details Updated",
                            type: "success",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                        setEditModal(false);
                        fetchRawMaterial();
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: "Server Problem. Please Try Again...",
                            type: "error",
                            timer: 2000,
                            showConfirmButton: false,
                        });
                    }
                    return;
                });
        } catch (error) {
            throw error;
        }
    };

    const deletRawMaterial = async () => {
        try {
            axios.delete(`master/raw_material_stocks/${Input.id}`).then((res) => {
                if (res.data.success === 1) {
                    Swal.fire({
                        icon: "success",
                        text: "Trims deleted",
                        type: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    fetchRawMaterial();
                    setDeleteModal(false);
                } else {
                    Swal.fire({
                        icon: "error",
                        text: "Server Problem. Please Try Again...",
                        type: "error",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                }
                return;
            });
        } catch (error) {
            throw error;
        }
    };

    // ✅ Data Mapping (Keeps symbol for the Screen Table)
    const data = Array.isArray(RawMaterial)
        ? RawMaterial.map((RM) => ({
            id: RM.raw_material_id,
            rname: RM.raw_material_name,
            stock: RM.stock_count,
            rate: RM.single_qty_rate || 0,
            price: RM.stock_price || 0,
            unit: RM.unit,
            status: RM.status,
            trims_type: RM.trims_type,
            size_id: RM.size_id,
            size_name: RM.size ? `${RM.size}` : "N/A",
            option: (<div style={{ display: "flex" }}>
                <FaPen className="text-amber-400 text-xl cursor-pointer" onClick={() => {
                    setEditModal(true);
                    if (Sizes.length === 0) fetchSizes();
                    setInput({
                        id: RM.raw_material_id,
                        rname: RM.raw_material_name,
                        stock: RM.stock_count,
                        rate: RM.single_qty_rate || 0, // Set rate for edit modal
                        unit: RM.unit,
                        trims_type: RM.trims_type,
                        status: RM.status,
                        size_id: RM.size_id,
                    });
                }} />
                <MdDeleteForever className="ml-2 text-red-500 text-2xl cursor-pointer" onClick={() => { setDeleteModal(true); setInput({ id: RM.raw_material_id }) }} />
            </div>)
        }))
        : [];

    const columns = [
        { name: "S.No", selector: (row, index) => index + 1, width: "70px" },
        { name: "Trims Name", selector: (row) => row.rname, sortable: true },
        { name: "Trims Type", selector: (row) => row.trims_type, sortable: true },
        { name: "Size", selector: (row) => row.size_name, sortable: true },
        { name: "Stock", selector: (row) => parseFloat(row.stock || 0).toFixed(0), sortable: true },
        { name: "Rate (₹)", selector: (row) => formatCurrency(row.rate), sortable: true },
        { name: "Total Price (₹)", selector: (row) => formatCurrency(row.price), sortable: true },
        { name: "Unit", selector: (row) => row.unit, sortable: true },
        {
            name: "Status", selector: (row) => row.status,
            cell: row => (
                <Badge color={row.status === "active" ? "success" : "failure"}>{row.status}</Badge>
            ), sortable: true
        },
        { name: "Option", selector: (row) => row.option },
    ];

    // ================= EXPORT LOGIC =================

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };
    const filteredExportData =
        trimsTypeFilter === "all"
            ? data
            : data.filter(
                (row) => row.trims_type === trimsTypeFilter
            );
    const today = formatDate(new Date());
    const totalStock = filteredExportData.reduce(
        (sum, row) => sum + (Number(row.stock) || 0),
        0
    );

    const overallTotalPrice = filteredExportData.reduce(
        (sum, row) => sum + (Number(row.price) || 0),
        0
    );
    // const overallTotalPrice = data.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
    const filename = "Trims_Stock_Report";

    const exportHeaders = ["S.No", "Trims Name", "Size", "Stock", "Rate", "Total Price ", "Unit", "Status"];

    const getExportData = () => {
        return filteredExportData.map((row, index) => [
            index + 1,
            row.rname || "-",
            row.size_name || "-",
            row.stock || "0",
            formatCurrency(row.rate, false), // ✅ Removed Symbol
            formatCurrency(row.price, false), // ✅ Removed Symbol
            row.unit || "-",
            row.status || "-"
        ]);
    };

    const handlePrint = () => {
        const win = window.open('', '_blank');
        const rows = filteredExportData.map((row, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${row.rname || "-"}</td>
                <td>${row.size_name || "-"}</td>
                <td>${row.stock || "0"}</td>
                <td>${formatCurrency(row.rate, false)}</td>
                <td>${formatCurrency(row.price, false)}</td>
                <td>${row.unit || "-"}</td>
                <td>${row.status || "-"}</td>
            </tr>
        `).join("");

        win.document.write(`
            <html>
                <head>
                    <title>${filename.replace(/_/g, ' ')}</title> 
                    <style>
                        @page { size: landscape; margin: 10mm; }
                        body { font-family: 'Segoe UI', Tahoma, sans-serif; color: #333; margin: 0; }
                        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #444; padding-bottom: 10px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th { background-color: #f2f2f2; font-weight: bold; text-transform: uppercase; font-size: 11px; border: 1px solid #ccc; padding: 10px; }
                        td { border: 1px solid #ccc; padding: 10px; text-align: left; font-size: 12px; }
                        .total-row { background-color: #eee; font-weight: bold; }
                        @media print { body { -webkit-print-color-adjust: exact; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2 style="margin:5px 0; color:#333;">${filename.replace(/_/g, ' ')}</h2>
                        <p>Date Generated: ${today}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                ${exportHeaders.map(h => `<th>${h}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${rows}
                            <tr class="total-row">
                                <td colspan="3" style="text-align: right;">OVERALL TOTAL:</td>
                                <td>${totalStock}</td>
                                <td>-</td>
                                <td>${formatCurrency(overallTotalPrice, false)}</td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);

        win.document.close();
        setTimeout(() => {
            win.focus();
            win.print();
            win.close();
        }, 250);
    };

    const downloadPDF = () => {
        const doc = new jsPDF("l", "pt", "a4");

        doc.setFontSize(16);
        doc.text(`${filename.replace(/_/g, ' ')}`, 40, 45);

        doc.setFontSize(10);
        doc.text(`Date: ${today}`, 730, 45);

        const tableRows = getExportData();

        // Add Total Row
        tableRows.push([
            { content: 'OVERALL TOTAL DETAILS', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: totalStock.toString(), styles: { fontStyle: 'bold' } },
            { content: '-', styles: { fontStyle: 'bold' } },
            { content: `${formatCurrency(overallTotalPrice, false)}`, styles: { fontStyle: 'bold' } }, // ✅ Removed Symbol
            { content: '', styles: { fontStyle: 'bold' } },
            { content: '', styles: { fontStyle: 'bold' } }
        ]);

        doc.autoTable({
            head: [exportHeaders],
            body: tableRows,
            startY: 65,
            theme: 'grid',
            styles: { fontSize: 9 },
            headStyles: { fillColor: [40, 40, 40] }
        });
        doc.save(`${filename}_${today}.pdf`);
    };

    const downloadCSV = () => {
        const rows = getExportData().map(row => row.map(cell => `"${cell}"`));
        // Empty cells for alignment before the totals
        rows.push(["", "", `"OVERALL TOTAL"`, `"${totalStock}"`, `"-"`, `"${formatCurrency(overallTotalPrice, false)}"`, `""`, `""`]); // ✅ Removed Symbol

        const csvContent = "\uFEFF" + [exportHeaders.map(h => `"${h}"`), ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${filename}_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={` ${isSidebarOpen ? "p-4 sm:ml-64 bg-content-bg min-h-screen " : "p-4  bg-content-bg min-h-screen"}  `} >

            <div className="p-4 rounded-lg dark:border-gray-700  mt-14">
                <div className="p-4 rounded-lg shadow bg-white">

                    {/* Top Action Bar: Add Button + Export Buttons */}
                    <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
                        <Button className="bg-icon-color  text-sidebar-color drop-shadow-md  font-bold" onClick={() => { setAddModal(true); setInput({}); if (Sizes.length === 0) fetchSizes(); }} >
                            Add Trims
                        </Button>

                        <div className="flex gap-2">
                            <Select
                                value={trimsTypeFilter}
                                onChange={(e) => setTrimsTypeFilter(e.target.value)}
                                className="min-w-[180px]"
                            >
                                <option value="all">All Trims</option>
                                <option value="general">General</option>
                                <option value="packing">Packing</option>
                                <option value="sewing">Sewing</option>
                                <option value="thread">Thread</option>
                            </Select>
                            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-black text-white rounded-lg transition-all shadow-md font-medium text-sm">
                                <span>🖨️</span> Print
                            </button>
                            <button onClick={downloadPDF} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">
                                <span>📄</span> PDF
                            </button>
                            <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-md font-medium text-sm">
                                <span>📥</span> CSV/Excel
                            </button>
                        </div>
                    </div>

                    <ReusableDataTable
                        title="Trims List"
                        columns={columns}
                        data={data}
                    />
                </div>
            </div>

            {/* ADD MODAL */}
            <Modal show={AddModal} size="md" onClose={() => { setAddModal(false); resetErrors(); }} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900 dark:text-white">Add Trims</h3>
                        <form onSubmit={addRawMaterial}>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="rname" value="Trims Name" />
                                </div>
                                <TextInput
                                    name="rname"
                                    id="rname"
                                    placeholder=""
                                    type="text"
                                    required
                                    onChange={onChangeValue}
                                    value={Input.rname || ''}
                                />
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="stock" value="Trims Type" />
                                </div>
                                <Select id="trims_type" name="trims_type" onChange={onChangeValue} required>
                                    <option value="">Select an option</option>
                                    <option value="general">General</option>
                                    <option value="packing">Packing</option>
                                    <option value="sewing">Sewing</option>
                                    <option value="thread">Thread</option>
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="size_id" value="Select Size" />
                                </div>
                                <Select id="size_id" name="size_id" onChange={onChangeValue} value={Input.size_id || ''}>
                                    <option value="">Select Size</option>
                                    {Sizes.map((s) => (
                                        <option key={s.size_id} value={s.size_id}>
                                            {s.size}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="stock" value="Stock" color={errors.stock && "failure"} />
                                </div>
                                <TextInput
                                    id="stock"
                                    name="stock"
                                    type="number"
                                    min={0}
                                    step="0.001"
                                    pattern="^\d+(\.\d{1,3})?$"
                                    onChange={onChangeValue}
                                    value={Input.stock || ''}
                                    color={errors.stock && "failure"}
                                    helperText={
                                        errors.stock && (
                                            <>
                                                <span className="font-medium"></span> {errors.stock}
                                            </>
                                        )
                                    }
                                    required />
                            </div>
                            {/* ✅ Added Rate Input */}
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="rate" value="Rate per Unit (₹)" />
                                </div>
                                <TextInput
                                    id="rate"
                                    name="rate"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    onChange={onChangeValue}
                                    value={Input.rate || ''}
                                    required
                                />
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="unit" value="Unit" />
                                </div>
                                <Select id="unit" name="unit" onChange={onChangeValue} value={Input.unit || ''} required>
                                    <option value="">Select an option</option>
                                    <option value="kg">KG</option>
                                    <option value="piece">Piece</option>
                                    <option value="meter">Meter</option>
                                    <option value="gross">Gross</option>
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="status" value="Status" />
                                </div>
                                <Select id="status" name="status" onChange={onChangeValue} value={Input.status || ''} required>
                                    <option value="">Select an option</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </Select>
                            </div>
                            <div className="pt-2 w-full flex justify-center">
                                <Button className="bg-icon-color  text-sidebar-color drop-shadow-md  font-bold" type="submit">Submit</Button>
                                <Button className="ml-2" color="gray" onClick={() => { setAddModal(false); resetErrors(); }}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            {/* EDIT MODAL */}
            <Modal show={EditModal} size="md" onClose={() => { setEditModal(false); resetErrors(); }} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="space-y-6">
                        <h3 className="text-xl font-medium text-gray-900 dark:text-white">Update Trims</h3>
                        <form onSubmit={UpdateRawMaterial}>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="rname" value="Trims Name" />
                                </div>
                                <TextInput
                                    name="rname"
                                    id="rname"
                                    placeholder=""
                                    type="text"
                                    required
                                    onChange={onChangeValue}
                                    value={Input.rname || ''}
                                />
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="stock" value="Trim Type" />
                                </div>
                                <Select id="trims_type" name="trims_type" onChange={onChangeValue} value={Input.trims_type} required>
                                    <option value="">Select an option</option>
                                    <option value="general">General</option>
                                    <option value="packing">Packing</option>
                                    <option value="sewing">Sewing</option>
                                    <option value="thread">Thread</option>
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="size_id" value="Select Size" />
                                </div>
                                <Select id="size_id" name="size_id" onChange={onChangeValue} value={Input.size_id || ''}>
                                    <option value="">Select Size</option>
                                    {Sizes.map((s) => (
                                        <option key={s.size_id} value={s.size_id}>
                                            {s.size}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="stock" value="Stock" color={errors.stock && "failure"} />
                                </div>
                                <TextInput
                                    id="stock"
                                    name="stock"
                                    type="number"
                                    min={0}
                                    step="0.001"
                                    pattern="^\d+(\.\d{1,3})?$"
                                    onChange={onChangeValue}
                                    color={errors.stock && "failure"}
                                    helperText={
                                        errors.stock && (
                                            <>
                                                <span className="font-medium"></span> {errors.stock}
                                            </>
                                        )
                                    }
                                    required
                                    value={Input.stock || ''} />
                            </div>
                            {/* ✅ Added Rate Input */}
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="rate" value="Rate per Unit (₹)" />
                                </div>
                                <TextInput
                                    id="rate"
                                    name="rate"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    onChange={onChangeValue}
                                    value={Input.rate || ''}
                                    required
                                />
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="unit" value="Unit" />
                                </div>
                                <Select id="unit" name="unit" onChange={onChangeValue} value={Input.unit || ''} required>
                                    <option value="">Select an option</option>
                                    <option value="kg">KG</option>
                                    <option value="piece">Piece</option>
                                    <option value="meter">Meter</option>
                                    <option value="gross">Gross</option>
                                </Select>
                            </div>
                            <div>
                                <div className="pt-2 mb-2 block">
                                    <Label htmlFor="status" value="Status" />
                                </div>
                                <Select id="status" name="status" value={Input.status || ''} onChange={onChangeValue} required>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </Select>
                            </div>
                            <div className="pt-2 w-full flex justify-center">
                                <Button className="bg-icon-color  text-sidebar-color drop-shadow-md   font-bold" type="submit">Submit</Button>
                                <Button className="ml-2" color="gray" onClick={() => { setEditModal(false); resetErrors(); }}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={DeleteModal} size="md" onClose={() => setDeleteModal(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="text-center">
                        <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
                        <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                            Are you sure you want to delete this raw material?
                        </h3>
                        <div className="flex justify-center gap-4">
                            <Button color="failure" onClick={() => deletRawMaterial()}>
                                {"Yes"}
                            </Button>
                            <Button color="gray" onClick={() => setDeleteModal(false)}>
                                No
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

        </div>
    );
}

export default RawMaterial;